'use client'

import { AnalyticsPage, useAnalyticsPageTracker } from '@components/analytics-provider'
import { AnalyticsPageNames, AnalyticsPageProps } from '@/app/utils/analytics'
import React, { useMemo } from 'react'

type AnalyticsPageTrackerProps = {
  pageName: AnalyticsPageNames
  pageProps?: AnalyticsPageProps
}

//polyfill isEqual from Lodash so we can use in middleware @ edge
function isAnalyticsPageEqual(current?: AnalyticsPage, prev?: AnalyticsPage): boolean {
  if (!current && !prev) {
    return true
  }

  if ((!current && prev) || (current && !prev)) {
    return false
  }

  if (current?.name !== prev?.name) {
    return false
  }

  const currentProps = current?.props as Record<string, unknown> | undefined
  const prevProps = prev?.props as Record<string, unknown> | undefined

  if (!currentProps || !prevProps) {
    return false
  }

  const currentKeys = Object.keys(currentProps)
  const prevKeys = Object.keys(prevProps)

  if (currentKeys.length !== prevKeys.length) {
    return false
  }

  for (const key of currentKeys) {
    if (currentProps[key] !== prevProps[key]) {
      return false
    }
  }

  return true
}

export const AnalyticsPageTracker = ({ pageName, pageProps }: AnalyticsPageTrackerProps): null => {
  const { setPage } = useAnalyticsPageTracker()

  // Memoize the page object to avoid extra page tracking
  const page: AnalyticsPage = useMemo(
    () => ({
      name: pageName,
      props: pageProps
    }),
    [pageName, pageProps]
  )

  const prevPage = React.useRef<AnalyticsPage | null>(null)
  React.useEffect(() => {
    // This should typically not be necessary, because page props are usually
    // coming from a server component and should not change during the lifetime
    // of the component however in cases where the page might call
    // router.refresh() we need to check if the page has changed to avoid
    // setting the page twice in the same session
    if (!isAnalyticsPageEqual(prevPage.current ?? undefined, page)) {
      setPage(page)
    }

    return () => {
      prevPage.current = page
    }
  }, [setPage, page])

  return null
}

// For backward compatibility
export const MixpanelPageTracker = AnalyticsPageTracker
